/**
 * Single Product Page Functionality
 * - Image gallery switching
 * - Variant selection (Size and Option)
 * - Quantity controls
 * - Mobile sticky CTA
 */

(function() {
  'use strict';

  /**
   * Initialize image gallery
   */
  function initImageGallery() {
    const mainImageContainer = document.querySelector('.single_product_view');
    const mainImage = mainImageContainer ? mainImageContainer.querySelector('img') : null;
    const thumbnails = document.querySelectorAll('.single_product_img_slide img');

    if (!mainImage || thumbnails.length === 0) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', function() {
        // Remove active class from all thumbnails
        thumbnails.forEach(t => t.classList.remove('active'));
        
        // Add active class to clicked thumbnail
        this.classList.add('active');
        
        // Fade out main image
        mainImageContainer.style.opacity = '0';
        
        // Change main image source after fade
        setTimeout(() => {
          // Use preloaded data attributes
          const newSrc = this.dataset.fullSrc || this.src.replace(/width=\d+/, 'width=1200');
          const newSrcset = this.dataset.fullSrcset || (
            this.src.replace(/width=\d+/, 'width=352') + ' 352w, ' +
            this.src.replace(/width=\d+/, 'width=832') + ' 832w, ' +
            newSrc + ' 1200w'
          );
          
          mainImage.src = newSrc;
          mainImage.srcset = newSrcset;
          
          // Update alt text if available
          if (this.alt) {
            mainImage.alt = this.alt;
          }
          
          mainImageContainer.style.opacity = '1';
        }, 300);
      });
    });
  }

  /**
   * Get product variants from JSON script (single product section)
   */
  function getVariantsData() {
    const script = document.getElementById('product-variants-json');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  /**
   * Get all option containers in option order.
   * Prefers data-option-index for explicit ordering; falls back to DOM order.
   */
  function getOptionContainers() {
    var all = document.querySelectorAll('.option_slect_for_product, .size_select_for_product');
    var arr = Array.from(all);
    var hasIndex = arr.length > 0 && arr[0].hasAttribute('data-option-index');
    if (hasIndex) {
      arr.sort(function(a, b) {
        return (parseInt(a.getAttribute('data-option-index'), 10) || 0) -
               (parseInt(b.getAttribute('data-option-index'), 10) || 0);
      });
    }
    return arr;
  }

  /**
   * Get current option selections from all variant option containers.
   * Returns array [option1Value, option2Value, option3Value].
   */
  function getCurrentSelections() {
    var containers = getOptionContainers();
    var selections = [];
    containers.forEach(function(container) {
      var active = container.querySelector('ul li.active');
      var value = active ? (active.getAttribute('data-value') || '').trim() : '';
      selections.push(value);
    });
    return selections;
  }

  /**
   * Find variant that matches current option selections
   */
  function findVariantFromSelections(variants, selections) {
    if (!variants || !Array.isArray(variants) || variants.length === 0) return null;
    return variants.find(v => {
      const o1 = (v.option1 || '').toString().trim();
      const o2 = (v.option2 || '').toString().trim();
      const o3 = (v.option3 || '').toString().trim();
      const s1 = (selections[0] || '').toString().trim();
      const s2 = (selections[1] || '').toString().trim();
      const s3 = (selections[2] || '').toString().trim();
      return o1 === s1 && o2 === s2 && o3 === s3;
    }) || null;
  }

  function findVariantIdFromSelections(variants, selections) {
    var match = findVariantFromSelections(variants, selections);
    return match ? match.id : null;
  }

  /**
   * Update the main product image to show the variant's featured image.
   * Also highlights the matching thumbnail if one exists.
   */
  function updateMainImage(variant) {
    if (!variant || !variant.featured_image) return;

    const mainImageContainer = document.querySelector('.single_product_view');
    const mainImage = mainImageContainer ? mainImageContainer.querySelector('img') : null;
    if (!mainImage) return;

    mainImageContainer.style.opacity = '0';

    setTimeout(function() {
      mainImage.src = variant.featured_image;
      if (variant.featured_image_srcset) {
        mainImage.srcset = variant.featured_image_srcset;
      }

      // Highlight the matching thumbnail
      var thumbnails = document.querySelectorAll('.single_product_img_slide img');
      thumbnails.forEach(function(thumb) {
        thumb.classList.remove('active');
        var thumbSrc = thumb.getAttribute('data-full-src') || thumb.getAttribute('data-src') || '';
        if (thumbSrc && variant.featured_image.indexOf(thumbSrc.split('?')[0].split('/').pop()) !== -1) {
          thumb.classList.add('active');
        }
      });

      // If no thumbnail matched, try matching by filename more loosely
      if (!document.querySelector('.single_product_img_slide img.active')) {
        var variantFilename = variant.featured_image.split('?')[0].split('/').pop();
        thumbnails.forEach(function(thumb) {
          var thumbFilename = (thumb.getAttribute('data-full-src') || thumb.src || '').split('?')[0].split('/').pop();
          if (thumbFilename === variantFilename) {
            thumb.classList.add('active');
          }
        });
      }

      mainImageContainer.style.opacity = '1';
    }, 300);
  }

  /**
   * Update the hidden variant id input from current option selections
   * and toggle sold-out state on add-to-cart buttons
   */
  function updateVariantInput() {
    const variants = getVariantsData();
    const input = document.getElementById('product_variant_id') || document.querySelector('#product_form input[name="id"]');
    if (!input) return;
    if (!variants || variants.length === 0) {
      return;
    }
    const selections = getCurrentSelections();
    const variant = findVariantFromSelections(variants, selections);
    if (variant != null) {
      input.value = variant.id;
      updateAddToCartState(variant.available !== false);
      updateMainImage(variant);
    }
  }

  /**
   * Enable or disable add-to-cart buttons based on variant availability
   */
  function updateAddToCartState(available) {
    var input = document.getElementById('product_variant_id');
    var defaultText = (input && input.getAttribute('data-add-to-cart-text')) || 'Add to Cart';
    var soldOutText = 'Sold Out';

    var desktopWrapper = document.getElementById('desktop-add-to-cart-wrapper');
    var mobileWrapper = document.getElementById('mobile-add-to-cart-wrapper');

    [desktopWrapper, mobileWrapper].forEach(function(wrapper) {
      if (!wrapper) return;
      var btn = wrapper.querySelector('button');
      if (!btn) return;
      var span = btn.querySelector('span');

      if (available) {
        btn.disabled = false;
        wrapper.classList.remove('jx-btn--disabled');
        if (span) span.textContent = defaultText;
      } else {
        btn.disabled = true;
        wrapper.classList.add('jx-btn--disabled');
        if (span) span.textContent = soldOutText;
      }
    });
  }

  /**
   * Initialize variant selectors and keep hidden variant id in sync
   */
  function initVariantSelectors() {
    var optionContainers = getOptionContainers();

    optionContainers.forEach(function(container) {
      var list = container.querySelector('ul');
      if (!list) return;
      var items = list.querySelectorAll('li');
      items.forEach(function(item) {
        item.addEventListener('click', function(e) {
          e.preventDefault();
          items.forEach(function(i) { i.classList.remove('active'); });
          this.classList.add('active');
          updateVariantInput();
        });
      });
      if (items.length > 0 && !list.querySelector('li.active')) {
        items[0].classList.add('active');
      }
    });

    updateVariantInput();
  }

  /**
   * Initialize quantity controls
   */
  function initQuantityControls() {
    // Desktop quantity controls
    const desktopDecrease = document.getElementById('decrease_qty');
    const desktopIncrease = document.getElementById('increase_qty');
    const desktopQuantity = document.getElementById('quantity_value');
    const quantityInput = document.getElementById('product_quantity');

    if (desktopDecrease && desktopIncrease && desktopQuantity) {
      desktopDecrease.addEventListener('click', function(e) {
        e.preventDefault();
        let value = parseInt(desktopQuantity.textContent) || 1;
        if (value > 1) {
          value--;
          desktopQuantity.textContent = value;
          if (quantityInput) quantityInput.value = value;
          
          // Update mobile quantity too
          const mobileQuantity = document.getElementById('quantity_value_mobile');
          if (mobileQuantity) mobileQuantity.textContent = value;
        }
      });

      desktopIncrease.addEventListener('click', function(e) {
        e.preventDefault();
        let value = parseInt(desktopQuantity.textContent) || 1;
        value++;
        desktopQuantity.textContent = value;
        if (quantityInput) quantityInput.value = value;
        
        // Update mobile quantity too
        const mobileQuantity = document.getElementById('quantity_value_mobile');
        if (mobileQuantity) mobileQuantity.textContent = value;
      });
    }

    // Mobile quantity controls
    const mobileDecrease = document.getElementById('decrease_qty_mobile');
    const mobileIncrease = document.getElementById('increase_qty_mobile');
    const mobileQuantity = document.getElementById('quantity_value_mobile');

    if (mobileDecrease && mobileIncrease && mobileQuantity) {
      mobileDecrease.addEventListener('click', function(e) {
        e.preventDefault();
        let value = parseInt(mobileQuantity.textContent) || 1;
        if (value > 1) {
          value--;
          mobileQuantity.textContent = value;
          if (quantityInput) quantityInput.value = value;
          
          // Update desktop quantity too
          if (desktopQuantity) desktopQuantity.textContent = value;
        }
      });

      mobileIncrease.addEventListener('click', function(e) {
        e.preventDefault();
        let value = parseInt(mobileQuantity.textContent) || 1;
        value++;
        mobileQuantity.textContent = value;
        if (quantityInput) quantityInput.value = value;
        
        // Update desktop quantity too
        if (desktopQuantity) desktopQuantity.textContent = value;
      });
    }
  }

  /**
   * Initialize mobile sticky CTA
   */
  function initMobileStickyCTA() {
    const cta = document.querySelector('.prize_show_mobile');
    const anchor = document.querySelector('.mobile_cta_anchor');

    // Check if elements exist first
    if (!cta || !anchor) return;
    
    // Only run on mobile/tablet
    if (window.innerWidth > 991) {
      cta.style.display = 'none';
      return;
    }

    let isFixed = false;
    const BUFFER = 10; // Small buffer to prevent flickering

    function updateCTA() {
      // Skip if we're on desktop
      if (window.innerWidth > 991) {
        cta.classList.remove('is-fixed', 'is-normal');
        cta.style.display = 'none';
        return;
      }

      // Get the viewport bottom position
      const viewportBottom = window.scrollY + window.innerHeight;
      
      // Get the anchor position
      const anchorRect = anchor.getBoundingClientRect();
      const anchorTop = anchorRect.top + window.scrollY;

      // If viewport bottom is above anchor, fix the CTA
      if (viewportBottom < (anchorTop - BUFFER)) {
        if (!isFixed) {
          cta.classList.add('is-fixed');
          cta.classList.remove('is-normal');
          isFixed = true;
        }
      } else {
        // Otherwise, keep it in normal flow
        if (isFixed) {
          cta.classList.remove('is-fixed');
          cta.classList.add('is-normal');
          isFixed = false;
        }
      }
    }

    // Initial state - start as normal
    cta.classList.add('is-normal');
    
    // Run initial check after a small delay to ensure proper layout
    setTimeout(updateCTA, 100);

    // Listen for scroll and resize
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          updateCTA();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
    
    window.addEventListener('resize', function() {
      updateCTA();
    });
  }

  /**
   * Initialize accordion
   */
  function initAccordion() {
    const accordionItems = document.querySelectorAll('.jholok_accordion_item');
    
    if (accordionItems.length === 0) return;
    
    accordionItems.forEach(item => {
      const header = item.querySelector('.jholok_accordion_header');
      const body = item.querySelector('.jholok_accordion_body');
      
      if (!header || !body) return;
      
      // Set initial max-height for items that are already open
      if (item.classList.contains('open')) {
        body.style.maxHeight = body.scrollHeight + 'px';
      }
      
      header.addEventListener('click', function() {
        const isOpen = item.classList.contains('open');
        
        if (isOpen) {
          // Close this item
          body.style.maxHeight = '0px';
          item.classList.remove('open');
        } else {
          // Close all other items
          accordionItems.forEach(otherItem => {
            const otherBody = otherItem.querySelector('.jholok_accordion_body');
            if (otherBody) {
              otherBody.style.maxHeight = '0px';
            }
            otherItem.classList.remove('open');
          });
          
          // Open this item
          item.classList.add('open');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });
  }

  /**
   * Initialize all functionality
   */
  function init() {
    initImageGallery();
    initVariantSelectors();
    initQuantityControls();
    initMobileStickyCTA();
    initAccordion();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId.includes('single-product')) {
      init();
    }
  });
})();
